<template>
  <div
    v-if="clientBrandingState.notFound"
    class="d-flex flex-column flex-root"
  >
    <div class="d-flex flex-column flex-column-fluid align-items-center justify-content-center p-10 text-center">
      <h1 class="text-dark mb-4">
        Nieznana domena klienta
      </h1>
      <p class="text-muted mb-0">
        Ta domena nie jest przypisana do panelu klienta.
      </p>
    </div>
  </div>
  <template v-else>
    <Loader />
    <div class="page d-flex flex-row flex-column-fluid">
      <Aside v-if="!$route.meta.hideMenu" />
      <div
        v-if="!$route.meta.hideMenu"
        id="kt_wrapper"
        class="d-flex flex-column flex-row-fluid wrapper"
      >
        <Header
          :page-name="pageName"
          @toggle-sidebar="openSidebarWithComponent"
        />
        <div
          id="kt_content"
          class="content d-flex flex-column flex-column-fluid"
        >
          <div class="post d-flex flex-column-fluid">
            <div class="container">
              <div class="sidebars-container">
                <SideBar
                  v-for="(sidebar, index) in sidebars"
                  :key="index"
                  :is-visible="true"
                  :title="sidebar.title"
                  :component-name="sidebar.componentName"
                  :position="sidebar.position"
                  :level="sidebar.level"
                  :props="sidebar.props"
                  @open-child="openSidebarWithComponent"
                  @close="closeSidebar"
                />
              </div>

              <router-view @pageName="setPageName" />
            </div>
          </div>
          <div
            v-if="$refs.scrollRef?.getState()"
            style="padding-bottom: 80px"
          />
        </div>
      </div>
      <router-view
        v-else
        @pageName="setPageName"
      />
    </div>
    <ScrollTop ref="scrollRef" />
  </template>
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance, provide, ref, watch } from 'vue';
import { useRoute } from 'vue-router'
import Aside from '@/components/header/Aside.vue'
import Header from '@/components/header/Header.vue'
import ScrollTop from '@/components/extras/ScrollTop.vue'
import Loader from '@/components/extras/Loader.vue'
import { DrawerComponent, MenuComponent } from '@/assets/ts/components'
import {axiosInstance} from '@/http-common'
import { AxiosError, AxiosResponse } from 'axios'
import moment from 'moment'
import {
  getCountMyUnReadNotificationsHistoryList,
  getDecodedJwtToken,
  getJwtToken,
  getMercureJwtToken,
  getCompanyFromJwt,
  getUserIdFromJwt,
  isJwtTokenFormatValid,
} from './crud/getters'
import SideBar from "@/components/header/SideBar.vue";
import SidebarItem from '@/types/SidebarItem';
import eventBus from "@/core/plugins/eventBus";
import { initSidebar, closeMobileMenuOnRouteChange } from '@/utils/sidebar-utils';
import { clientBrandingState } from '@/utils/client-branding';

interface DecodedToken {
  exp: any;  // assuming exp is a number
}

export default defineComponent({
  name: 'App',
  components: {
    SideBar,
    Aside,
    Header,
    ScrollTop,
    Loader
  },
  setup() {
    const route = useRoute()
    const scrollRef = ref(null)
    const { proxy } = getCurrentInstance() as any;

    watch(() => route.path, () => {
      proxy.clearSidebars();
      MenuComponent.hideDropdowns(undefined)
      DrawerComponent.hideAll()
      closeMobileMenuOnRouteChange()
    })

    return { scrollRef, clientBrandingState }
  },
  data() {
    return {
      pageName: '',
      sidebars: [] as SidebarItem[],
      tokenRefreshInterval: null as any,
      eventSource: null as EventSource | null,
      mercureUrl: process.env.VUE_APP_MERCURE_BASE_URL,
      mercureReconnectAttempts: 0,
      mercureMaxReconnectAttempts: 5,
      mercureReconnectTimeout: null as any,
      mercureHeartbeatInterval: null as any,
      lastMercureMessageTime: null as number | null,
    }
  },
  methods: {
    clearSidebars() {
      this.sidebars = [];
    },
    cleanupDrawerOverlays() {
      const overlays = document.querySelectorAll('.drawer-overlay');
      if (overlays.length > 1) {
        // Usuń wszystkie overlays oprócz ostatniego
        for (let i = 0; i < overlays.length - 1; i++) {
          overlays[i].remove();
        }
      }
    },
    openSidebarWithComponent(obj: SidebarItem) {

      if (typeof obj === 'object' && obj !== null) {

        if (obj.level === 0) {
          this.clearSidebars();
        } else {
          this.sidebars = this.sidebars.filter((sidebar) => sidebar.level < obj.level);
        }

        this.sidebars.push({
          level: obj.level,
          componentName: obj.componentName,
          position: obj.position || 'right',
          title: obj.title  || '',
          props: obj.props,
        });

      }
    },
    closeSidebar(level) {
      this.sidebars = this.sidebars.filter((sidebar) => sidebar.level < level);
    },
    async isTokenValid() {
      const token = getJwtToken();
      const decodedToken: any = getDecodedJwtToken();
      if (token && decodedToken) {
        try {
          const tokenExp = decodedToken.exp!;
          const currentTimestamp = Math.floor(Date.now() / 1000);
          const timeLeft = tokenExp - currentTimestamp;

          if (timeLeft < 120) { // 120 s = 2 m
            try {
              axiosInstance.defaults.baseURL = process.env.VUE_APP_API_BASE_URL;
              axiosInstance.defaults.headers['Authorization'] = 'Bearer ' + token;

              const renewResponse = await axiosInstance.post('/api/v1/renew');
              const renewedData = renewResponse.data?.token;
              if (isJwtTokenFormatValid(renewedData)) {
                localStorage.setItem('jwt_token', renewedData);
              }
            } catch (err) {
              const axiosError = err as AxiosError;
              if (axiosError.response?.status === 401) { // unauthorized, token expired
                this.clearSidebars();
                localStorage.clear();
                this.$router.push('login');
              } else {
                console.error('Error renewing token:', axiosError.message);
                setTimeout(() => this.isTokenValid(), 30000); // 30 s
              }
            }
          }
        } catch (error) {
          console.error("Error decoding token:", error);
        }
      }
    },
    async isMercureTokenValid() {
      if (localStorage.getItem('mercureToken')) {
        const token = getJwtToken();
        const decodedToken: any = getDecodedJwtToken();
        if (token && decodedToken) {
          try {
            const token_exp: number = decodedToken.exp!;

            const current_timestamp: number = Math.floor(Date.now() / 1000);
            if (token_exp <= current_timestamp) { // If Mercure token has expired
              try {
                const jwtToken = localStorage.getItem('jwt_token');
                if (!jwtToken) throw new Error("JWT token is missing");

                axiosInstance.defaults.baseURL = process.env.VUE_APP_API_BASE_URL;
                axiosInstance.defaults.headers['Authorization'] = 'Bearer ' + jwtToken;

                const renewResponse = await axiosInstance.post('/api/v1/renew');
                const renewedData = renewResponse.data?.token;
                if (!isJwtTokenFormatValid(renewedData)) {
                  return;
                }

                localStorage.setItem('jwt_token', renewedData);

                const newMercureToken = await getMercureJwtToken();
                if (newMercureToken) {
                  localStorage.setItem('mercureToken', newMercureToken);
                }

                // Reconnect Mercure with new token
                this.mercureReconnectAttempts = 0; // Reset reconnect attempts
                this.subscribeToMercure();
              } catch (err) {
                const axiosError = err as AxiosError;
                if (axiosError.response?.status === 401) { // unauthorized, jwt token expired
                  this.closeMercureConnection();
                  this.clearSidebars();
                  localStorage.clear();
                  this.$router.push('login');
                } else {
                  console.error("Error renewing Mercure token:", err);
                }
              }
            }
          } catch (error) {
            console.error("Error decoding mercure token:", error);
          }
        }

      } else {
        const newMercureToken = await getMercureJwtToken();
        if (newMercureToken) {
          localStorage.setItem('mercureToken', newMercureToken);
        }
      }
    },

    setPageName(name) {
      this.pageName = name
    },
    getCountMyUnReadNotificationsHistoryList,
    async getUnread() {
      try {
        const response = await this.getCountMyUnReadNotificationsHistoryList()
        return response.status === 200 ? response.data : 0;
      } catch {
        return 0;
      }
    },
    loadUnreadNotificationsCount() {
      this.getUnread().then((count) => {
        window.unreadNotifications = count;
        window.dispatchEvent(new Event('unreadNotificationsUpdated'));
      });
    },
    closeMercureConnection() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      if (this.mercureReconnectTimeout) {
        clearTimeout(this.mercureReconnectTimeout);
        this.mercureReconnectTimeout = null;
      }
      if (this.mercureHeartbeatInterval) {
        clearInterval(this.mercureHeartbeatInterval);
        this.mercureHeartbeatInterval = null;
      }
    },
    async subscribeToMercure() {
      const companyId = getCompanyFromJwt();
      const userId = getUserIdFromJwt();
      const mercureToken = localStorage.getItem('mercureToken') || localStorage.getItem('mercure_token');

      if (!companyId || !mercureToken) {
        return;
      }

      // Close existing connection if any
      this.closeMercureConnection();

      try {
        const url = new URL(`${this.mercureUrl}/.well-known/mercure`);
        url.searchParams.append('topic', `company/${companyId}`);
        // Add user-specific topic for asset notifications
        if (userId) {
          url.searchParams.append('topic', `user/${userId}`);
        }
        const eventSourceInitDict: any = {
          headers: {
            'Authorization': 'Bearer ' + mercureToken
          }
        };

        this.eventSource = new EventSource(url, eventSourceInitDict as EventSourceInit);

        this.eventSource.onopen = () => {
          this.mercureReconnectAttempts = 0;
          this.lastMercureMessageTime = Date.now();

          // Start heartbeat monitoring
          this.startMercureHeartbeat();
        };

        this.eventSource.onmessage = (event) => {
          this.lastMercureMessageTime = Date.now();

          // Check if it's a comment (heartbeat) or actual data
          if (event.data && event.data.trim() !== '') {
            // Refresh unread count when new notification arrives
            this.loadUnreadNotificationsCount();
            // Emit event for NotificationsHistoryMenu to refresh
            eventBus.emit("newNotificationReceived");
          }
        };

        this.eventSource.onerror = () => {
          this.closeMercureConnection();

          // Attempt reconnect with exponential backoff
          if (this.mercureReconnectAttempts < this.mercureMaxReconnectAttempts) {
            this.mercureReconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, this.mercureReconnectAttempts - 1), 30000); // Max 30s

            this.mercureReconnectTimeout = setTimeout(() => {
              this.subscribeToMercure();
            }, delay);
          }
        };
      } catch (error) {
        // Silent fail
      }
    },
    startMercureHeartbeat() {
      // Check connection health every 60 seconds
      this.mercureHeartbeatInterval = setInterval(() => {
        const now = Date.now();
        const timeSinceLastMessage = this.lastMercureMessageTime ? now - this.lastMercureMessageTime : null;

        // If no message received in 90 seconds (Mercure sends heartbeat every ~15s), reconnect
        if (timeSinceLastMessage && timeSinceLastMessage > 90000) {
          this.mercureReconnectAttempts = 0; // Reset attempts for heartbeat timeout
          this.subscribeToMercure();
        }
      }, 60000); // Check every 60s
    }
  },
  mounted() {
    const self = this
    this.isTokenValid()
    this.isMercureTokenValid()
    this.tokenRefreshInterval = setInterval(() => {
      self.isTokenValid()
      self.isMercureTokenValid()
    }, 30000) // 30000 ms = 30 sek

    moment.locale(localStorage.getItem('lang') as string)

    // Inicjalizacja motywu ciemnego
    const isDarkTheme = localStorage.getItem('digitrack_theme_dark') === 'true';
    if (isDarkTheme) {
      document.body.classList.add('dark-theme');
    }

    // Inicjalizacja sidebar utilities (opóźnienie dla pewności że DOM jest gotowy)
    setTimeout(() => {
      try {
        initSidebar();
      } catch (error) {
        console.error('Sidebar init error:', error);
      }
    }, 100);

    window.clearSidebars = self.clearSidebars;
    window.openSidebarWithComponent = self.openSidebarWithComponent;

    this.loadUnreadNotificationsCount();
    window.addEventListener("loadUnreadNotificationsCount", this.loadUnreadNotificationsCount);

    // Subscribe to Mercure for real-time notifications
    this.subscribeToMercure();

    // Close Mercure connection when user closes tab/window
    window.addEventListener('beforeunload', () => {
      this.closeMercureConnection();
    });

    // Obserwuj zmiany DOM dla drawer-overlay
    const observer = new MutationObserver(() => {
      this.cleanupDrawerOverlays();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup overlays on drawer toggle
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target && (target.id === 'kt_aside_mobile_toggle' || target.closest('#kt_aside_mobile_toggle'))) {
        setTimeout(() => {
          this.cleanupDrawerOverlays();
        }, 100);
      }

      // Cleanup when clicking on overlay
      if (target && target.classList.contains('drawer-overlay')) {
        setTimeout(() => {
          const allOverlays = document.querySelectorAll('.drawer-overlay');
          allOverlays.forEach(overlay => overlay.remove());
        }, 100);
      }
    });
  },
  updated() {
    this.isTokenValid()
    this.isMercureTokenValid()
  },
  beforeUnmount() {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval)
    }
    this.closeMercureConnection();
    window.removeEventListener("loadUnreadNotificationsCount", this.loadUnreadNotificationsCount);
  }
})
</script>

<style lang="scss">
@import './assets/css/_metronic.css';
@import './assets/scss/style.scss';
@import "assets/scss/modal/base";
@import '@/assets/scss/bs-tooltip.scss';
@import '@/assets/scss/sidebar-style.scss';
@import '@/assets/scss/dark-theme-style.scss';
@import '@/assets/scss/asset-modbus-param-set.scss';
@import '@/assets/scss/asset-dkn-control-panel.scss';

.aside {
  z-index: 1001;
}

@media (max-width: 768px) {
  #kt_aside {
    z-index: 9999 !important;
  }

  #kt_aside[data-kt-drawer-shown="true"] {
    z-index: 9999 !important;
    display: block !important;
    transform: translateX(0) !important;
    visibility: visible !important;
  }

  .drawer-overlay {
    z-index: 9998 !important;
  }

  [data-kt-drawer="true"][data-kt-drawer-shown="true"] {
    display: block !important;
    visibility: visible !important;
  }
}

/* Zapobieganie podwójnym overlay'om */
.drawer-overlay + .drawer-overlay {
  display: none !important;
}

/* Styl dla głównego kontenera z zawartością */
#kt_content {
  height: 100vh;
  width: 100%;
}

.sidebars-container {
  display: flex;
  width: 100vw;
  overflow: hidden;
}

@media (max-width: 768px) {
  .sidebars-container {
    flex-direction: column;
    overflow: visible;
  }

  body.map #kt_wrapper {
    margin-left: 0 !important;
  }

  body.map #kt_content {
    padding-top: 70px !important;
  }

  body.login-page .sidebars-container {
    display: none !important;
  }

  body.login-page #kt_wrapper {
    margin-left: 0 !important;
    height: 100vh !important;
  }

  body.login-page #kt_content {
    padding: 0 !important;
    height: 100vh !important;
  }

  body.login-page .container {
    height: auto !important;
    overflow: visible !important;
    padding: 0 !important;
  }

  body.login-page .post {
    height: 100vh !important;
  }
}

</style>
